import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../components/ui/select';
import { Loader2 } from 'lucide-react';
import { useToast } from '../../../hooks/use-toast';
import { PhoneInput } from '../../../components/ui/phone-input';
import { User, UserRole, CreateUserDto, UpdateUserDto, usersApi } from '../../../api/users.api';

interface UserDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    user: User | null; // If null, we are creating a new user
    onSuccess: () => void;
}

export function UserDialog({ open, onOpenChange, user, onSuccess }: UserDialogProps) {
    const { toast } = useToast();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        username: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        role: UserRole.USER,
        pin: '',
    });

    useEffect(() => {
        if (open && user) {
            setFormData({
                username: user.username,
                firstName: user.firstName || '',
                lastName: user.lastName || '',
                email: user.email,
                phone: user.tel,
                role: user.role,
                pin: '', // Can't view PIN
            });
        } else if (open && !user) {
            setFormData({
                username: '',
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                role: UserRole.USER,
                pin: '',
            });
        }
    }, [open, user]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (user) {
                // Update
                const updateData: UpdateUserDto = {
                    username: formData.username !== user.username ? formData.username : undefined,
                    firstName: formData.firstName !== user.firstName ? formData.firstName : undefined,
                    lastName: formData.lastName !== user.lastName ? formData.lastName : undefined,
                    email: formData.email !== user.email ? formData.email : undefined,
                    tel: formData.phone !== user.tel ? formData.phone : undefined,
                    role: formData.role !== user.role ? formData.role : undefined,
                };

                // Only send if there are changes
                if (Object.keys(updateData).length > 0) {
                    await usersApi.updateUser(user.id, updateData);
                    toast({ title: 'Siksè', description: 'Enfòmasyon modifye', type: 'success' });
                } else {
                    onOpenChange(false);
                    return;
                }
            } else {
                // Create
                const createData: CreateUserDto = {
                    email: formData.email,
                    phone: formData.phone,
                    username: formData.username,
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    role: formData.role,
                    pin: formData.pin || undefined, // Optional
                };
                await usersApi.createUser(createData);
                toast({ title: 'Siksè', description: 'Itilizatè kreye', type: 'success' });
            }
            onSuccess();
            onOpenChange(false);
        } catch (error: any) {
            console.error(error);
            const msg = error.response?.data?.message || 'Yon erè rive';
            toast({ title: 'Erè', description: msg, type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-[95vw] max-w-[500px] max-h-[90vh] overflow-y-auto">
                <form onSubmit={handleSubmit}>
                    <DialogHeader className="text-center pb-2">
                        <DialogTitle className="text-lg">{user ? 'Modifye Itilizatè' : 'Ajoute Nouvo Itilizatè'}</DialogTitle>
                        <DialogDescription className="text-sm">
                            {user
                                ? 'Modifye enfòmasyon pèsonèl ak wòl itilizatè a.'
                                : 'Ranpli fòmilè a pou kreye yon nouvo kont itilizatè.'}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        {/* Prenom & Non - stack on mobile */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label htmlFor="firstName" className="text-sm font-medium">Prenom</Label>
                                <Input
                                    id="firstName"
                                    value={formData.firstName}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                                    placeholder="Jean"
                                    className="h-10"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="lastName" className="text-sm font-medium">Non</Label>
                                <Input
                                    id="lastName"
                                    value={formData.lastName}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                                    placeholder="Baptiste"
                                    className="h-10"
                                />
                            </div>
                        </div>

                        {/* Username & Role - stack on mobile */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label htmlFor="username" className="text-sm font-medium">
                                    Non itilizatè <span className="text-red-500">*</span>
                                </Label>
                                <Input
                                    id="username"
                                    value={formData.username}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                                    placeholder="jdoe"
                                    required
                                    className="h-10"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="role" className="text-sm font-medium">Wòl</Label>
                                <Select
                                    value={formData.role}
                                    onValueChange={(val: UserRole) => setFormData(prev => ({ ...prev, role: val }))}
                                >
                                    <SelectTrigger className="h-10">
                                        <SelectValue placeholder="Chwazi wòl" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value={UserRole.USER}>User (Kliyan)</SelectItem>
                                        <SelectItem value={UserRole.AGENT}>Agent</SelectItem>
                                        <SelectItem value={UserRole.SUPER_AGENT}>Super Agent</SelectItem>
                                        <SelectItem value={UserRole.CAISSIER}>Caissier</SelectItem>
                                        <SelectItem value={UserRole.SUPPORT}>Support</SelectItem>
                                        <SelectItem value={UserRole.ADMIN}>Admin</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Email */}
                        <div className="space-y-1.5">
                            <Label htmlFor="email" className="text-sm font-medium">
                                Email <span className="text-red-500">*</span>
                            </Label>
                            <Input
                                id="email"
                                type="email"
                                value={formData.email}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                                placeholder="email@example.com"
                                required
                                className="h-10"
                            />
                        </div>

                        {/* Phone */}
                        <div className="space-y-1.5">
                            <Label htmlFor="phone" className="text-sm font-medium">
                                Telefòn <span className="text-red-500">*</span>
                            </Label>
                            <PhoneInput
                                id="phone"
                                value={formData.phone}
                                onChange={(value: string | undefined) => setFormData(prev => ({ ...prev, phone: value || '' }))}
                                defaultCountry="HT"
                                placeholder="Nimewo telefòn"
                            />
                        </div>

                        {/* PIN - only for create */}
                        {!user && (
                            <div className="border-t pt-4 bg-slate-50 p-3 rounded-lg">
                                <div className="space-y-1.5">
                                    <Label htmlFor="pin" className="text-sm font-medium">PIN (Opsyonèl)</Label>
                                    <Input
                                        id="pin"
                                        type="password"
                                        maxLength={4}
                                        value={formData.pin}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, pin: e.target.value }))}
                                        placeholder="1234"
                                        className="h-10"
                                    />
                                </div>
                                <p className="text-xs text-slate-500 mt-2">
                                    Si w pa mete PIN, itilizatè a ap resevwa yon lyen pou konfigire li.
                                </p>
                            </div>
                        )}
                    </div>

                    <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 pt-4 border-t">
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto">
                            Annile
                        </Button>
                        <Button type="submit" disabled={loading} className="w-full sm:w-auto min-w-[140px]">
                            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {user ? 'Sovgade' : 'Kreye Itilizatè'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
